import React, { useEffect, useMemo, useState } from 'react';
import DatePicker from '../DatePicker/DatePicker';
import { FINANCIALS_DATE_PRESETS, getDefaultFinancialsDateRange } from '../../utils/financialsDateRange';
import '../../styles/FormPage.css';
import '../../components/FinancialsDateRangeFilter/FinancialsDateRangeFilter.css';
import './FinancialsFiltersModal.css';

function FinancialsFiltersModal({
  isOpen,
  onClose,
  mode = 'reports',
  values,
  companies = [],
  clients = [],
  projects = [],
  onApply,
  onClear,
}) {
  const [draftFromDate, setDraftFromDate] = useState(values.fromDate);
  const [draftToDate, setDraftToDate] = useState(values.toDate);
  const [draftCompanyId, setDraftCompanyId] = useState(values.companyId || '');
  const [draftClientId, setDraftClientId] = useState(values.clientId || '');
  const [draftProjectId, setDraftProjectId] = useState(values.projectId || '');
  const [activePreset, setActivePreset] = useState('');

  useEffect(() => {
    if (!isOpen) {
      return;
    }

    setDraftFromDate(values.fromDate);
    setDraftToDate(values.toDate);
    setDraftCompanyId(values.companyId || '');
    setDraftClientId(values.clientId || '');
    setDraftProjectId(values.projectId || '');
    setActivePreset('');
  }, [isOpen, values]);

  const filteredProjects = useMemo(() => {
    if (!draftClientId) {
      return projects;
    }
    return projects.filter(
      (project) => String(project.clientId) === String(draftClientId)
    );
  }, [projects, draftClientId]);

  if (!isOpen) {
    return null;
  }

  const handleDateChange = (event) => {
    const { name, value } = event.target;
    setActivePreset('');
    if (name === 'fromDate') {
      setDraftFromDate(value);
      return;
    }
    setDraftToDate(value);
  };

  const applyPreset = (preset) => {
    const range = preset.getRange();
    setActivePreset(preset.id);
    setDraftFromDate(range.fromDate);
    setDraftToDate(range.toDate);
  };

  const handleClientChange = (event) => {
    const { value } = event.target;
    setDraftClientId(value);

    if (value && draftProjectId) {
      const projectStillValid = projects.some(
        (project) =>
          String(project.id) === String(draftProjectId) &&
          String(project.clientId) === String(value)
      );
      if (!projectStillValid) {
        setDraftProjectId('');
      }
    }
  };

  const handleProjectChange = (event) => {
    const { value } = event.target;
    setDraftProjectId(value);

    if (value) {
      const project = projects.find((item) => String(item.id) === String(value));
      if (project?.clientId) {
        setDraftClientId(String(project.clientId));
      }
    }
  };

  const handleApply = () => {
    onApply({
      fromDate: draftFromDate,
      toDate: draftToDate,
      companyId: draftCompanyId,
      clientId: draftClientId,
      projectId: draftProjectId,
    });
    onClose();
  };

  const handleClear = () => {
    const defaultRange = getDefaultFinancialsDateRange();
    const defaultCompanyId = companies.length > 0 ? String(companies[0].id) : '';

    setDraftFromDate(defaultRange.fromDate);
    setDraftToDate(defaultRange.toDate);
    setDraftCompanyId(defaultCompanyId);
    setDraftClientId('');
    setDraftProjectId('');
    setActivePreset('');

    onClear({
      fromDate: defaultRange.fromDate,
      toDate: defaultRange.toDate,
      companyId: defaultCompanyId,
      clientId: '',
      projectId: '',
    });
    onClose();
  };

  return (
    <div className="form-page-modal-overlay" onClick={onClose}>
      <div
        className="form-page-modal wide financials-filters-modal"
        onClick={(event) => event.stopPropagation()}
      >
        <div className="form-page-modal-head">
          <div>
            <h2>Report filters</h2>
            <p>
              {mode === 'client-report'
                ? 'Choose date range, client, project, and company for this report.'
                : 'Choose date range and company for this report.'}
            </p>
          </div>
          <button type="button" className="form-page-btn-ghost" onClick={onClose}>
            Close
          </button>
        </div>

        <div className="financials-filters-modal-body">
          <div className="financials-filters-modal-section">
            <h3>Date range</h3>
            <div className="financials-filters-modal-row">
              <div className="financials-filter-field">
                <label htmlFor="filter-fromDate">From date</label>
                <DatePicker
                  id="filter-fromDate"
                  name="fromDate"
                  value={draftFromDate}
                  onChange={handleDateChange}
                />
              </div>
              <div className="financials-filter-field">
                <label htmlFor="filter-toDate">To date</label>
                <DatePicker
                  id="filter-toDate"
                  name="toDate"
                  value={draftToDate}
                  onChange={handleDateChange}
                />
              </div>
            </div>
            <div className="financials-filters-preset-row">
              {FINANCIALS_DATE_PRESETS.map((preset) => (
                <button
                  key={preset.id}
                  type="button"
                  className={`financials-date-preset-btn${activePreset === preset.id ? ' is-active' : ''}`}
                  onClick={() => applyPreset(preset)}
                >
                  {preset.label}
                </button>
              ))}
            </div>
          </div>

          {mode === 'client-report' && (
            <div className="financials-filters-modal-section">
              <h3>Client & project</h3>
              <div className="financials-filters-modal-row">
                <div className="financials-filter-field">
                  <label htmlFor="filter-clientId">Client</label>
                  <select
                    id="filter-clientId"
                    value={draftClientId}
                    onChange={handleClientChange}
                  >
                    <option value="">Select client</option>
                    {clients.map((client) => (
                      <option key={client.id} value={client.id}>
                        {client.name}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="financials-filter-field">
                  <label htmlFor="filter-projectId">Project (optional)</label>
                  <select
                    id="filter-projectId"
                    value={draftProjectId}
                    onChange={handleProjectChange}
                  >
                    <option value="">
                      {draftClientId ? 'All projects for client' : 'Select project'}
                    </option>
                    {filteredProjects.map((project) => (
                      <option key={project.id} value={project.id}>
                        {project.name}
                      </option>
                    ))}
                  </select>
                </div>
              </div>
              <p className="financials-filters-modal-hint">
                Select a client to include all of their projects, or choose a specific project.
              </p>
            </div>
          )}

          {companies.length > 0 && (
            <div className="financials-filters-modal-section">
              <h3>Company</h3>
              <div className="financials-filter-field">
                <label htmlFor="filter-companyId">Company</label>
                <select
                  id="filter-companyId"
                  value={draftCompanyId}
                  onChange={(event) => setDraftCompanyId(event.target.value)}
                >
                  <option value="">All</option>
                  {companies.map((company) => (
                    <option key={company.id} value={String(company.id)}>
                      {company.name}
                    </option>
                  ))}
                </select>
              </div>
            </div>
          )}
        </div>

        <div className="form-page-modal-foot">
          <button type="button" className="form-page-btn-secondary" onClick={handleClear}>
            Clear
          </button>
          <button type="button" className="form-page-btn" onClick={handleApply}>
            Apply filters
          </button>
        </div>
      </div>
    </div>
  );
}

export default FinancialsFiltersModal;
